import React from 'react';

import { Box, Button, Grid } from '@mui/material';
import { DateTime } from 'luxon';

import { Field, Form } from '@hrlogics/react-components/form';

import { DateFormatPatterns, STATUSES_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber } from '@clearverify/utils';

import {
  EmployerStaffDocument,
  EmployerStaffRole,
  UserStatus,
  useUpdateUserRoleMutation,
} from 'src/api';
import { DetailGridItem, FullscreenLoader } from 'src/components/common';
import { SelectField } from 'src/components/finalForm';
import { EMPLOYER_ROLE_OPTIONS } from 'src/constants/employer';
import { useNotification } from 'src/hooks/app';
import { useEmployerStaff } from 'src/hooks/employerStaff';
import { useCurrentUser, useCurrentUserPermissions } from 'src/hooks/user';
import { isEnableStatus } from 'src/utils/user';

import { FormFields, FormValues } from './types';

export interface EmployerStaffDetailsModalFormProps {
  onCloseModal: () => void;
  employerStaffId: string | undefined;
  switchClosability: (closability: boolean) => void;
}

export const EmployerStaffDetailsModalForm: React.FC<EmployerStaffDetailsModalFormProps> = ({
  switchClosability,
  onCloseModal,
  employerStaffId,
}) => {
  const { createApolloContext } = useNotification();

  const { user: currentUser, isUserLoading } = useCurrentUser();
  const { employerStaff, loading } = useEmployerStaff(employerStaffId);

  const [updateUserRole] = useUpdateUserRoleMutation({
    refetchQueries: [{ query: EmployerStaffDocument, variables: { id: employerStaffId } }],
    context: createApolloContext({
      successMessage: 'Employer Staff has been successfully updated',
    }),
  });

  const {
    isPlatformAdmin: isCurrentUserClearVerifyAdmin,
    hasPlatformRoles,
    isEmployerAdmin,
    isPeoSuperAdmin,
    isPeoAdmin,
    isPeoPayrollProviderUser,
    loading: isCurrentUserPermissionsLoading,
  } = useCurrentUserPermissions();

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      try {
        const userId = employerStaff?.user.id || '';
        const currentStatus = employerStaff?.user.status;
        const newStatus = formValues.userStatus;

        const hasStatusChanged = currentStatus && currentStatus !== newStatus;

        await updateUserRole({
          variables: {
            userId,
            data: {
              employerStaffRole: formValues.businessRole,
              ...(hasStatusChanged ? { status: newStatus } : null),
            },
          },
        });

        switchClosability(true);
        onCloseModal();
      } catch {
        switchClosability(true);
      }
    },
    [
      employerStaff?.user.id,
      employerStaff?.user.status,
      onCloseModal,
      switchClosability,
      updateUserRole,
    ],
  );

  const { firstName, lastName, birthDate, phoneNumber, email, status } = employerStaff?.user ?? {};

  const initialValues: FormValues = React.useMemo(
    () => ({
      [FormFields.userStatus]: employerStaff?.user.status || UserStatus.Inactive,
      [FormFields.businessRole]: employerStaff?.role || EmployerStaffRole.EmployerManager,
    }),
    [employerStaff?.role, employerStaff?.user.status],
  );

  // The roles the server lets change an employer staff member's status; managers are not among them.
  const canChangeStatus =
    hasPlatformRoles ||
    isPeoSuperAdmin ||
    isPeoAdmin ||
    isPeoPayrollProviderUser ||
    (isEmployerAdmin && employerStaff?.user.id !== currentUser?.id);
  const shouldRenderStatusField = canChangeStatus && isEnableStatus(status);

  const shouldRenderSelectSection = isCurrentUserClearVerifyAdmin || shouldRenderStatusField;

  if (loading || isUserLoading || isCurrentUserPermissionsLoading) {
    return <FullscreenLoader />;
  }

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit}>
      {({ dirty, submitting }) => (
        <Grid container spacing={1}>
          <Grid size={12}>
            <DetailGridItem topText="First Name" bottomText={firstName} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Last Name" bottomText={lastName} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Email Address" bottomText={email} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem
              topText="Date of birth"
              bottomText={
                birthDate &&
                DateTime.fromISO(birthDate).toFormat(DateFormatPatterns.monthDayYearDots)
              }
            />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Phone Number" bottomText={formatPhoneNumber(phoneNumber)} />
          </Grid>

          {shouldRenderSelectSection ? (
            <Grid container my={1} spacing={2} size={12}>
              {shouldRenderStatusField ? (
                <Grid size={12}>
                  <Field
                    name={FormFields.userStatus}
                    component={SelectField}
                    label="Status"
                    options={STATUSES_OPTIONS}
                    fullWidth
                  />
                </Grid>
              ) : null}

              {isCurrentUserClearVerifyAdmin ? (
                <Grid size={12}>
                  <Field
                    name={FormFields.businessRole}
                    component={SelectField}
                    label="Role"
                    options={EMPLOYER_ROLE_OPTIONS}
                    fullWidth
                  />
                </Grid>
              ) : null}
            </Grid>
          ) : null}

          <Box width="100%" display="flex" justifyContent="center" gap={2} mt={4}>
            <Button variant="outlined" color="error" onClick={onCloseModal} disabled={submitting}>
              Cancel
            </Button>
            {shouldRenderStatusField ? (
              <Button
                variant="contained"
                type="submit"
                loading={submitting}
                disabled={submitting || !dirty}
              >
                Save Changes
              </Button>
            ) : null}
          </Box>
        </Grid>
      )}
    </Form>
  );
};
